import Box from '@mui/material/Box';
import { alpha, useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useMemo } from 'react';
import { NAVY, STATUS_GOOD } from 'styles/tokens';

interface Execution {
  endpoint: string;
  executionTime: number;
  status: string;
  createdAt: string;
}

interface RecentExecutionsGraphProps {
  statuses: string[];
  executions: Execution[];
  integrationId: string;
}

const RecentExecutionsGraph: React.FC<RecentExecutionsGraphProps> = ({ statuses, executions, integrationId }) => {
  const theme = useTheme();
  // Calculate timeline marks only for days (midnight marks) with overlap prevention
  const timelineMarks = useMemo(() => {
    if (executions.length === 0) return [];

    const allMarks: Array<{ executionIndex: number; label: string; position: number }> = [];
    const processedMarks = new Set<string>(); // Track which day marks we've already processed

    // First, collect all potential day marks
    executions.forEach((execution, index) => {
      const execTime = new Date(execution.createdAt).getTime();
      if (!execTime) return;

      const execDate = new Date(execTime);
      const hours = execDate.getHours();
      const dateStr = execDate.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
      const dateKey = execDate.toDateString();

      // Only check for midnight (00:00) - first execution at or after midnight
      const midnightKey = `${dateKey}-0`;
      if (!processedMarks.has(midnightKey)) {
        if (hours === 0) {
          processedMarks.add(midnightKey);
          const position = index * 4.5 + 2;
          allMarks.push({ executionIndex: index, label: dateStr, position });
        } else {
          // First execution after midnight but before next midnight
          const todayMidnight = new Date(execDate);
          todayMidnight.setHours(0, 0, 0, 0);
          const hasMidnightExecution = executions.slice(0, index).some((e) => {
            const eTime = new Date(e.createdAt).getTime();
            return eTime >= todayMidnight.getTime() && new Date(e.createdAt).getHours() === 0;
          });
          if (!hasMidnightExecution) {
            processedMarks.add(midnightKey);
            const position = index * 4.5 + 2;
            allMarks.push({ executionIndex: index, label: dateStr, position });
          }
        }
      }
    });

    // Filter out overlapping marks (minimum spacing of ~40px to prevent overlap)
    const MIN_SPACING = 40;
    const filteredMarks: Array<{ executionIndex: number; label: string; position: number }> = [];

    allMarks.forEach((mark) => {
      const isTooClose = filteredMarks.some(
        (existingMark) => Math.abs(mark.position - existingMark.position) < MIN_SPACING
      );
      if (!isTooClose) {
        filteredMarks.push(mark);
      }
    });

    return filteredMarks;
  }, [executions]);

  // Calculate the width needed for bars (4px width + 0.5 gap per bar)
  const barsContainerWidth = useMemo(() => statuses.length * 4.5, [statuses.length]);

  // Reverse arrays to show newer requests on the right
  const reversedStatuses = useMemo(() => [...statuses].reverse(), [statuses]);
  const reversedExecutions = useMemo(() => [...executions].reverse(), [executions]);

  // Recalculate timeline marks positions for reversed order
  const reversedTimelineMarks = useMemo(() => {
    return timelineMarks.map((mark) => {
      // Calculate new position: from right side (newer requests)
      const reversedIndex = statuses.length - 1 - mark.executionIndex;
      const position = reversedIndex * 4.5 + 2;
      return { ...mark, position };
    });
  }, [timelineMarks, statuses.length]);

  // Early return after all hooks
  if (statuses.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          gap: 0.5,
          height: 32,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: alpha(NAVY, 0.6),
          }}
        >
          No hay ejecuciones recientes
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{
          color: alpha(NAVY, 0.6),
          display: 'block',
          mb: 0.5,
        }}
      >
        Últimas Ejecuciones ({statuses.length})
      </Typography>
      <Box
        sx={{
          overflowX: 'auto',
          overflowY: 'hidden',
          '&::-webkit-scrollbar': {
            height: 6,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(NAVY, 0.2),
            borderRadius: 3,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
            height: 32,
            alignItems: 'flex-end',
            position: 'relative',
            minWidth: barsContainerWidth,
          }}
        >
          {reversedStatuses.map((status, index) => {
            const isSuccess = status === 'success';
            const barColor = isSuccess ? STATUS_GOOD : theme.palette.error.main;
            const execution = reversedExecutions[index];

            return (
              <Tooltip
                key={`${integrationId}-${status}-${index}`}
                title={
                  execution
                    ? `${isSuccess ? 'Éxito' : 'Error'} - ${execution.endpoint} (${execution.executionTime}ms)`
                    : `${isSuccess ? 'Éxito' : 'Error'} - Request ${index + 1}`
                }
                arrow
              >
                <Box
                  sx={{
                    minWidth: 4,
                    width: 4,
                    height: '24px',
                    backgroundColor: barColor,
                    borderRadius: 0.5,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      opacity: 0.8,
                      transform: 'scaleY(1.1)',
                    },
                  }}
                />
              </Tooltip>
            );
          })}
        </Box>
        {/* Timeline */}
        {reversedTimelineMarks.length > 0 && (
          <Box
            sx={{
              position: 'relative',
              height: 20,
              mt: 0.5,
              minWidth: barsContainerWidth,
              pl: 2,
              pr: 2,
            }}
          >
            {reversedTimelineMarks.map((mark, index) => {
              // Adjust transform for edge cases to prevent clipping
              const isNearLeftEdge = mark.position < 30;
              const isNearRightEdge = mark.position > barsContainerWidth - 30;
              let transform = 'translateX(-50%)';
              if (isNearLeftEdge) {
                transform = 'translateX(0)';
              } else if (isNearRightEdge) {
                transform = 'translateX(-100%)';
              }

              return (
                <Box
                  key={`timeline-${mark.executionIndex}-${mark.label}-${index}`}
                  sx={{
                    position: 'absolute',
                    left: `${mark.position}px`,
                    transform,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: 0.05,
                      height: 8,
                      bgcolor: alpha(NAVY, 0.2),
                      mb: 0.25,
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: alpha(NAVY, 0.6),
                      fontSize: '0.65rem',
                      whiteSpace: 'nowrap',
                      lineHeight: 1,
                    }}
                  >
                    {mark.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default RecentExecutionsGraph;
